import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const Select = forwardRef(
  (
    {
      label,
      error,
      required = false,
      className = "",
      selectClassName = "",
      onFocus: propOnFocus,
      onBlur: propOnBlur,
      onChange: propOnChange,
      defaultValue,
      value: controlledValue, // Controlled component için value prop
      options = [],
      placeholder = "Seçiniz",
      showPlaceholder = true,
      disabledPlaceholder = true,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslationDetector();
    const [isFocused, setIsFocused] = useState(false);

    // Internal ref for our own use
    const internalRef = useRef(null);

    // Forward the ref to both React Hook Form and our internal use
    useImperativeHandle(ref, () => internalRef.current);

    // Select'te label her zaman floating olacak
    const shouldFloat = true;

    const handleFocus = useCallback(
      (e) => {
        setIsFocused(true);
        propOnFocus?.(e);
      },
      [propOnFocus],
    );

    const handleBlur = useCallback(
      (e) => {
        setIsFocused(false);
        propOnBlur?.(e);
      },
      [propOnBlur],
    );

    const handleChange = useCallback(
      (e) => {
        propOnChange?.(e);
      },
      [propOnChange],
    );

    // Memoized class names to prevent unnecessary clsx recalculations
    const generateSelectClassName = useCallback(
      () =>
        clsx(
          "peer h-[56px] w-full appearance-none rounded-md border p-4 font-medium focus:ring-2 focus:outline-none",
          // Background color - disabled durumunda bg-gray-100, normal durumda bg-white
          props.disabled ? "bg-background-global" : "bg-white",
          // Native select arrow'unu tamamen gizle (cross-browser)
          "[&::-webkit-appearance]:none [&::-moz-appearance]:none bg-no-repeat",
          "bg-[length:0] bg-[position:0]", // Background arrow'u tamamen kaldır
          // Focus colors - error durumunda danger, normal durumda primary
          error
            ? "focus:border-danger focus:ring-danger border-danger"
            : "focus:border-primary focus:ring-primary border-border",
          props.disabled && "cursor-not-allowed",
          label && "pt-4",
          // Select arrow için padding
          "pr-12",
          selectClassName,
        ),
      [error, props.disabled, label, selectClassName],
    );

    const labelClassName = useCallback(
      () =>
        clsx(
          "pointer-events-none absolute left-4 flex items-center gap-1 transition-all duration-200",
          // Select'te label her zaman floating
          "-top-2 px-2 text-xs",
          !props.disabled && "bg-white",
          error
            ? "text-danger"
            : isFocused
              ? "text-primary"
              : "text-foreground-muted",
          props.disabled && "text-gray-400",
        ),
      [error, props.disabled, isFocused],
    );

    return (
      <div className={clsx("flex flex-col gap-2", className)}>
        <div className="relative">
          <select
            ref={internalRef}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            {...(controlledValue !== undefined
              ? { value: controlledValue || "" }
              : { defaultValue })}
            className={generateSelectClassName()}
            {...props}
          >
            {/* Placeholder option - sadece options varsa göster */}
            {showPlaceholder &&
              options &&
              Array.isArray(options) &&
              options.length > 0 && (
                <option value="" disabled={disabledPlaceholder}>
                  {placeholder}
                </option>
              )}

            {/* Options from props */}
            {options && Array.isArray(options) && options.length > 0 ? (
              options.map((option) => {
                // Option object format: { value, label } veya string format
                const optionValue =
                  typeof option === "object" ? option.value : option;
                const optionLabel =
                  typeof option === "object" ? option.label : option;

                return (
                  <option key={optionValue} value={optionValue}>
                    {optionLabel}
                  </option>
                );
              })
            ) : (
              // Options yoksa veya boşsa sadece placeholder göster
              <option value="" disabled>
                {placeholder}
              </option>
            )}
          </select>

          {/* Custom select arrow */}
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <FontAwesomeIcon
              icon={byPrefixAndName.fas["chevron-down"]}
              className={clsx(
                "h-4 w-4 transition-all duration-200 ease-in-out",
                // Rotation durumu
                isFocused && "rotate-180",
                // Color durumu
                error
                  ? "text-danger"
                  : isFocused
                    ? "text-primary"
                    : "text-foreground-muted",
                props.disabled && "text-gray-400",
              )}
            />
          </div>

          {label && (
            <label htmlFor={props.id} className={labelClassName()}>
              {props.disabled && (
                <span className="absolute top-1/2 left-0 block h-px w-full bg-white"></span>
              )}
              <span className="relative z-[1]">{label}</span>
            </label>
          )}
        </div>

        {error && (
          <p className="text-danger flex items-center gap-1 pl-4 text-xs">
            <FontAwesomeIcon icon={byPrefixAndName.far["circle-exclamation"]} />
            <span>
              {typeof error === "string" ? error : t("common.required")}
            </span>
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";

// Memo ile props karşılaştırması
const MemoizedSelect = memo(Select);
MemoizedSelect.displayName = "Select";

export default MemoizedSelect;
